import { useCallback, useRef, useState } from "react";

interface UseLongPressSwipeOptions {
    initial?: number;
    min?: number;
    max?: number;
    step?: number;
    longPressMs?: number;
    sensitivity?: number;
    onChange?: (value: number) => void;
    onLongPressStart?: () => void;
}

export const useLongPressSwipe = (options: UseLongPressSwipeOptions = {}) => {
    const {
        initial = 1,
        min = 0,
        max = 10,
        step = 1,
        longPressMs = 280,
        sensitivity = 180,
        onChange,
        onLongPressStart,
    } = options;

    // Поточне значення, яке віддаємо назовні
    const [value, setValue] = useState(initial);

    // Ref для актуального значення (щоб не залежати від ререндерів)
    const valueRef = useRef(initial);

    // X координата в момент pointerDown
    const startXRef = useRef(0);

    // Значення, з якого починаємо свайп
    const startValueRef = useRef(initial);

    // Чи активувався long-press (чи можна вже міняти значення)
    const isActiveRef = useRef(false);

    // Таймер для long-press
    const timerRef = useRef<number | null>(null);

    // id активного pointer (для pointer capture)
    const pointerId = useRef<number | null>(null);

    // DOM-елемент, на якому почався long-press
    const targetRef = useRef<HTMLElement | null>(null);

    // Обмежуємо значення в межах min/max
    const clamp = useCallback(
        (v: number) => Math.min(max, Math.max(min, v)),
        [min, max],
    );

    // Округляємо значення до кроку (step)
    const snap = useCallback(
        (v: number) => Math.round(v / step) * step,
        [step],
    );

    // Оновлюємо значення під час свайпу
    const updateValue = useCallback(
        (v: number) => {
            const next = clamp(snap(v));

            // Якщо різниця мізерна — ігноруємо, щоб не спамити оновленнями
            if (Math.abs(next - valueRef.current) < step * 0.01) return;

            valueRef.current = next;
            setValue(next);
            onChange?.(next);
        },
        [clamp, snap, step, onChange],
    );

    // Вішається тільки на PresetsRow — звідси стартує long-press
    const onTriggerPointerDown = useCallback(
        (e: React.PointerEvent) => {
            if (!e.isPrimary) return;

            // Запамʼятовуємо стартову позицію пальця
            startXRef.current = e.clientX;

            // Запамʼятовуємо значення, з якого починаємо
            startValueRef.current = valueRef.current;

            // Поки що long-press не активний
            isActiveRef.current = false;

            // Зберігаємо pointerId для capture
            pointerId.current = e.pointerId;

            // Запамʼятовуємо елемент, на якому почали жест
            targetRef.current = e.currentTarget as HTMLElement;

            // Запускаємо таймер long-press
            timerRef.current = window.setTimeout(() => {
                isActiveRef.current = true;

                // Захоплюємо pointer, щоб рух ловився навіть поза елементом
                targetRef.current?.setPointerCapture(pointerId.current!);

                // Колбек на старт long-press (показати slider і т.д.)
                onLongPressStart?.();
            }, longPressMs);
        },
        [longPressMs, onLongPressStart],
    );

    // Вішається на Root — відстежує рух після активації
    const onRootPointerMove = useCallback(
        (e: React.PointerEvent) => {
            if (!isActiveRef.current || !e.isPrimary) return;

            // Наскільки зсунули палець по X
            const deltaX = e.clientX - startXRef.current;

            // Діапазон значень (max - min)
            const range = max - min;

            // Конвертуємо пікселі в зміну значення
            const deltaValue = (deltaX / sensitivity) * range;

            updateValue(startValueRef.current + deltaValue);
        },
        [sensitivity, min, max, updateValue],
    );

    // Загальна функція очищення стану жесту
    const clear = useCallback(() => {
        // Скасовуємо таймер long-press
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        // Відпускаємо pointer capture
        if (pointerId.current !== null && targetRef.current) {
            try {
                targetRef.current.releasePointerCapture(pointerId.current);
            } catch {
                // ignore (може впасти, якщо вже відпущений)
            }
        }

        isActiveRef.current = false;
        pointerId.current = null;
        targetRef.current = null;
    }, []);

    // Обробка відпускання пальця / миші
    const onRootPointerUp = useCallback(() => {
        clear();
    }, [clear]);

    // Публічний сеттер (для кліку по пресету)
    const setValuePublic = useCallback(
        (v: number) => {
            const next = clamp(snap(v));
            valueRef.current = next;
            setValue(next);
            // onChange тут НЕ викликаємо — це не свайп, а ручна установка
        },
        [clamp, snap],
    );

    // Bind для PresetsRow — тільки стартує long-press
    const triggerBind = {
        onPointerDown: onTriggerPointerDown,
        style: { touchAction: "none" } as React.CSSProperties,
    };

    // Bind для Root — ловить рух і завершення жесту
    const moveBind = {
        onPointerMove: onRootPointerMove,
        onPointerUp: onRootPointerUp,
        onPointerCancel: onRootPointerUp,
        style: { touchAction: "none" } as React.CSSProperties,
    };

    return { value, triggerBind, moveBind, setValue: setValuePublic };
};
